/** Remove markdown bold markers: **text** → text */
function stripMarkdownBold(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/gs, "$1");
}

/** Escape plain text for safe insertion into HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type LetterPrintOptions = {
  /** Reserved for future PDF-specific hints; print dialog is the same for PDF save. */
  forPdf?: boolean;
  /** Document title (iframe document). */
  title?: string;
};

function buildLetterPrintHtml(escapedBody: string, docTitle: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(docTitle)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
    }
    body {
      font-family: "Times New Roman", Times, serif;
      font-size: 12pt;
      line-height: 1.5;
      padding: 0.75in;
      max-width: 8.5in;
      margin: 0 auto;
    }
    .letter-body {
      white-space: pre-wrap;
      word-wrap: break-word;
    }
    @media print {
      body {
        padding: 0.5in;
        max-width: none;
      }
    }
  </style>
</head>
<body>
  <div class="letter-body">${escapedBody}</div>
</body>
</html>`;
}

/**
 * Prints letter content via a hidden iframe (no pop-up).
 * Writes HTML, then calls iframe.contentWindow.print(); removes iframe after printing.
 */
export function printLetterInHiddenIframe(body: string, options?: LetterPrintOptions): boolean {
  const trimmed = body.trim();
  if (!trimmed) {
    return false;
  }

  const escaped = escapeHtml(stripMarkdownBold(trimmed));
  const docTitle = options?.title ?? "Dispute letter";
  const html = buildLetterPrintHtml(escaped, docTitle);

  const iframe = document.createElement("iframe");
  iframe.setAttribute(
    "style",
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;visibility:hidden;"
  );
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const contentWin = iframe.contentWindow;
  const contentDoc = iframe.contentDocument ?? contentWin?.document;
  if (!contentWin || !contentDoc) {
    iframe.remove();
    return false;
  }

  contentDoc.open();
  contentDoc.write(html);
  contentDoc.close();

  let removed = false;

  const removeIframe = () => {
    if (removed) {
      return;
    }
    removed = true;
    contentWin.removeEventListener("afterprint", removeIframe);
    iframe.remove();
  };

  contentWin.addEventListener("afterprint", removeIframe);

  const runPrint = () => {
    contentWin.focus();
    contentWin.print();
  };

  if (contentDoc.readyState === "complete") {
    window.requestAnimationFrame(() => window.requestAnimationFrame(runPrint));
  } else {
    contentWin.addEventListener("load", runPrint, { once: true });
  }

  return true;
}
